import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import NeonText from '../UI/NeonText';

interface NavItem {
    path: string;
    label: string;
    code: string;
}

const Navbar: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const location = useLocation();

    const navItems: NavItem[] = [
        { path: '/', label: 'Inicio', code: '// home' },
        { path: '/services', label: 'Servicios', code: '// services' },
        { path: '/portfolio', label: 'Portafolio', code: '// portfolio' },
        { path: '/about', label: 'Nosotros', code: '// about' },
        { path: '/contact', label: 'Contacto', code: '// contact' },
    ];

    // Definimos las variantes con el tipo correcto de Framer Motion
    const menuVariants: Variants = {
        closed: {
            opacity: 0,
            height: 0,
            transition: {
                duration: 0.3,
                ease: "easeInOut"
            }
        },
        open: {
            opacity: 1,
            height: "auto",
            transition: {
                duration: 0.3,
                ease: "easeInOut"
            }
        }
    };

    // Variantes para las líneas del botón hamburguesa
    const topLineVariants = {
        closed: { rotate: 0, y: 0 },
        open: { rotate: 45, y: 6 }
    };

    const middleLineVariants = {
        closed: { opacity: 1 },
        open: { opacity: 0 }
    };

    const bottomLineVariants = {
        closed: { rotate: 0, y: 0 },
        open: { rotate: -45, y: -6 }
    };

    return (
        <nav className="navbar glassmorphism">
            <div className="nav-container">
                {/* Logo */}
                <Link to="/" className="nav-logo">
                    <span className="logo-brackets">{'</>'}</span>
                    <NeonText variant="cyan" className="logo-text">
                        TECH_ANIME
                    </NeonText>
                </Link>

                {/* Menú Desktop */}
                <div className="nav-menu">
                    {navItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-link ${
                                location.pathname === item.path ? 'active' : ''
                            }`}
                        >
                            <span className="nav-code">{item.code}</span>
                            <span className="nav-label">{item.label}</span>
                            {location.pathname === item.path && (
                                <motion.div
                                    className="nav-indicator"
                                    layoutId="navIndicator"
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                />
                            )}
                        </Link>
                    ))}
                </div>

                {/* Menú Mobile Button */}
                <button
                    className="mobile-menu-btn"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label="Toggle menu"
                >
                    <motion.span
                        variants={topLineVariants}
                        animate={isMenuOpen ? "open" : "closed"}
                        transition={{ duration: 0.3 }}
                    />
                    <motion.span
                        variants={middleLineVariants}
                        animate={isMenuOpen ? "open" : "closed"}
                        transition={{ duration: 0.3 }}
                    />
                    <motion.span
                        variants={bottomLineVariants}
                        animate={isMenuOpen ? "open" : "closed"}
                        transition={{ duration: 0.3 }}
                    />
                </button>

                {/* Menú Mobile */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            className="mobile-menu"
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={menuVariants}
                        >
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`mobile-nav-link ${
                                        location.pathname === item.path ? 'active' : ''
                                    }`}
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <span className="mobile-nav-code">{item.code}</span>
                                    <span className="mobile-nav-label">{item.label}</span>
                                </Link>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </nav>
    );
};

export default Navbar;